import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ProjectProvider } from "@/context/ProjectContext";
import DashboardLayout from "./pages/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Pelanggan from "./pages/Pelanggan";
import Sebutharga from "./pages/Sebutharga";
import Invoice from "./pages/Invoice";
import NilaiKosProjek from "./pages/NilaiKosProjek";
import KosDetail from "./pages/KosDetail";
import LaporanKerja from "./pages/LaporanKerja";
import SiteVisit from "./pages/SiteVisit";
import GambarProjek from "./pages/GambarProjek";
import HutangPelanggan from "./pages/HutangPelanggan";
import DataBackup from "./pages/DataBackup";
import NotFound from "./pages/NotFound";
import Login, { getAuthState } from "./pages/Login";
import UserManagement from "./pages/UserManagement";
import ActivityLogs from "./pages/ActivityLogs";
import Analytics from "./pages/Analytics";
import GlobalSearch from "./pages/GlobalSearch";
import SystemMonitor from "./pages/SystemMonitor";
import { KeyboardShortcutsModal } from "@/components/KeyboardShortcutsModal";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const auth = getAuthState();
  return auth.isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

function AppRoutes({ isLoggedIn, showShortcuts, setShowShortcuts }: { isLoggedIn: boolean; showShortcuts: boolean; setShowShortcuts: (open: boolean) => void }) {
  // Keyboard shortcuts - now inside Router context
  useKeyboardShortcuts({
    isAuthenticated: isLoggedIn,
    onShowHelp: () => setShowShortcuts(true),
    onLogout: () => {
      localStorage.removeItem('auth_state');
      window.location.href = '/login';
    },
  });

  return (
    <>
      <KeyboardShortcutsModal open={showShortcuts} onOpenChange={setShowShortcuts} />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            isLoggedIn ? (
              <DashboardLayout />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/pelanggan" element={<Pelanggan />} />
          <Route path="/sebutharga" element={<Sebutharga />} />
          <Route path="/invoice" element={<Invoice />} />
          <Route path="/nilai-kos-projek" element={<NilaiKosProjek />} />
          <Route path="/kos-detail" element={<KosDetail />} />
          <Route path="/laporan-kerja" element={<LaporanKerja />} />
          <Route path="/site-visit" element={<SiteVisit />} />
          <Route path="/gambar-projek" element={<GambarProjek />} />
          <Route path="/hutang-pelanggan" element={<HutangPelanggan />} />
          <Route path="/data-backup" element={<DataBackup />} />
          <Route path="/pengurusan-pengguna" element={<UserManagement />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/global-search" element={<GlobalSearch />} />
          <Route path="/system-monitor" element={<SystemMonitor />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(() => getAuthState().isLoggedIn);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const auth = getAuthState();
      setIsLoggedIn(auth.isLoggedIn);
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ProjectProvider>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <AppRoutes isLoggedIn={isLoggedIn} showShortcuts={showShortcuts} setShowShortcuts={setShowShortcuts} />
          </BrowserRouter>
        </TooltipProvider>
      </ProjectProvider>
    </QueryClientProvider>
  );
}

export default App;